import { datadogLogs } from "@datadog/browser-logs";
import type { LanguageCode } from "@frontend/lib/graphql/generated-gql";

export enum SlotReviewStatusEnum {
  NeedsReschedule = "NEEDS_RESCHEDULE",
}
// Backend limitation for stress address length
export const MAX_ADDRESS_LENGTH = 60;

const SUPPORTED_LANGUAGES: LanguageCodeWithFrench[] = [
  "en" as LanguageCodeWithFrench, // english
  "es" as LanguageCodeWithFrench, // spanish
  "ru" as LanguageCodeWithFrench, // russian
  "zh" as LanguageCodeWithFrench, // chinese simplified
  "zh_HK" as LanguageCodeWithFrench, // chinese traditional
  "fr" as LanguageCodeWithFrench, // french
  "ht" as LanguageCodeWithFrench, // haitian creole
];

export enum LanguageCodeWithFrench {
  En = "en",
  Es = "es",
  Ru = "ru",
  Zh = "zh",
  ZhHk = "zh_HK",
  Fr = "fr",
  Ht = "ht",
}

export const policyConstants = {
  cancellation:
    "If your do not cancel your appointment at least 24 hours before, or if you are a no-show, we will assess you a $50 missed appointment fee.",
};

export const year1900 = new Date(1900, 0, 1, 0, 0, 0, 0);

// The threshold for the delta between the PAID insured price and the new insured price.
// If it is within this threshold, we will show the paid price instead.
export const insuredPriceDeltaThreshold = 10;

/**
 * EXTENSION ONLY
 * - PUT ON CONFIG MANAGER ASAP AFTER EXTENSION LAUNCH!
 */

export const HUDSONCONSTANTS = {
  BODY: "Our body protocols are built for those striving to achieve peak performance goals, enhance physical endurance, build muscle mass, burn fat, manage weight or maintain a youthful glow.",
  RESILIENCE:
    "Our resilience protocols are built for those recovering from chronic illness, experiencing pain and inflammation, or taking on a more proactive approach to building long-term immunity.",
  MIND: "Our mind protocols are built for those battling environmental factors, daily stress and anxiety, and taxing work. Select neuro boost if you are interested in biohacking.",
};

export type MessageParams = Record<string, string | boolean>;

export const SORIN_PC_PROCEDURE_POPULARITY = {
  "Follow Up (15 min)": 1440,
  "Sorin Heart Scan": 423,
  "Vein Mapping": 168,
  Ultimate: 163,
  "Physical Therapy Follow Up - 15 min": 151,
  "New Patient Consultation": 147,
  "Peripheral Angiogram": 136,
  ECHO: 130,
  "New Patient (30 Min)": 125,
  "Telemedicine (30 min)": 122,
  Supreme: 116,
  "Physical Therapy Follow Up": 97,
  "Follow-Up Consultation": 94,
  "Blood Draw": 76,
  "Follow Up (30 min)": 50,
  Arterial: 48,
  "Physical Therapy New Patient": 46,
  "Vein Ablation": 46,
  "Ankle Brachial Index (ABI)": 41,
  Varithena: 36,
  "Ankle Brachial Index": 36,
  "Follow-Up (20 Min)": 31,
  Injection: 30,
  HEARTFLOW: 29,
  "Physical Therapy": 26,
  "Follow Up (30 Min)": 21,
  "New Patient (15-30 min)": 20,
  "Peripheral Venogram": 18,
  "Genicular artery embolization": 18,
  Legacy: 17,
  "HEARTFLOW - Insurance Authorized": 16,
  EKG: 15,
  "Physical Therapy Follow Up - 30 min": 9,
  "Uterine Fibroid Embolization": 8,
  "Loop Recorder Implant": 6,
  "Carotid Ultrasound": 6,
  "BLOOD + EKG": 6,
  "New Patient (15 Min)": 5,
  "Venous Doppler": 4,
  "Outstanding Balance": 3,
  "Abdominal CT Scan": 3,
  "New Patient (45-60 min)": 2,
  "EKG without consult": 2,
  "Unna Boots": 1,
};

export const HUDSON_EXTENSION_PROCEDURE_POPULARITY = {
  "Peptide Pick Up": 382,
  "DEXA Scan (Body Composition only)": 93,
  "Longevity Consult - Follow-Up, In Person": 89,
  "Longevity Consult - New Client, In Person": 85,
  "HBOT - 1 session": 69,
  "Comprehensive Diagnostics Package": 57,
  "Neuro Reset (without Ketamine IV)": 48,
  "DEXA Scan (Body Composition + Bone Density)": 39,
  "Longevity Consult - Follow-Up, Virtual": 35,
  "Ozone Therapy - 1 Session": 23,
  "HBOT 20 Pack - 1 Session": 20,
  "HBOT 10 Pack - 1 Session": 20,
  "Resting Metabolic Rate Test": 17,
  "Longevity Consult - New Client, Virtual": 16,
  "Regenerative Consult - Follow-Up, In-Person": 16,
  "Neuro Reset Complete (with Ketamine IV)": 15,
  "FREE Generation Labs": 14,
  "VO2 Max Test": 13,
  "DEXA Scan (Bone Density only)": 12,
  "Ketamine IV": 11,
  "Custom IV (Physician Curated) Infusion": 11,
  "Plasmapheresis - 1 Session": 11,
  "HBOT 5 Pack - 1 Session": 11,
  "Medication Refill - Virtual": 10,
  "Blood Draw": 10,
  "Methylene Blue IV": 8,
  "Ozone Therapy 3 Pack - 1 Session": 6,
  "3D Postural Alignment Scan": 6,
  "Immunity Blend": 6,
  "Cerebrolysin IV": 6,
  "FREE 3D Postural Alignment Scan": 5,
  "Core Longevity Bloodwork": 4,
  "EXTENSION Health - Signature IV": 4,
  "Botox (Full Face)": 3,
  "NAD Consult": 3,
  "Neuro Reset Complete (with Ketamine IV, with RFA)": 2,
  "Weight Management Consult - New Client, In-Person": 2,
  "Vitamin Infusion": 2,
  "Baseline Diagnostics Package": 2,
  SGB: 2,
  "Ozone Therapy FU - 1 session": 2,
  "Energy Blend": 2,
  "Weight Management Package": 2,
  "Ozone Therapy - 3 Sessions": 1,
  "DEXA Scan Follow Up (Body Composition + Bone Density)": 1,
  "Weight Management Consult - New Client, Virtual": 1,
  "HBOT - Bring a friend": 1,
  "Weight Management Consult - Follow-Up, Virtual": 1,
  "Advanced Bloodwork Consult": 1,
  "VO2 Max Test - Follow Up": 1,
  "Medical Consult (West Village) - Follow Up": 1,
  "Stem Cell Injection (Spine Region) - Basic": 1,
  "Stem Cell Injection (Joint Tendon)": 1,
  "Generation Labs": 1,
  "NAD IV (500mg)": 1,
  "Regenerative Consult - New Client, In Person": 1,
  "NAD IV (1000mg)": 1,
  "NAD IV (250mg)": 1,
  "DEXA Body Composition Scan + FREE 3D Postural Alignment": 1,
  "PRP Injection (Joint Tendon) - Advanced": 1,
  "PRP Consult": 1,
  "HBOT Consultation": 1,
  "Galleri Multi-Cancer Test": 1,
};

export const HUDSON_MIND_PROCEDURE_POPULARITY = {
  "HOLD 15": 105,
  Spravato: 87,
  "Ketamine IV": 78,
  "Virtual Check In (15 Mins)": 45,
  "Virtual Check In (30 Mins)": 28,
  "TMS - Single Visit (15 mins)": 26,
  "New Patient Evaluation - Dr. Green, Lead Psychiatrist": 23,
  "SGB - 1 Injection": 21,
  "Virtual New Patient Evaluation - Dr. Green, Lead Psychiatrist": 20,
  "In-Person Check In (15 Mins)": 14,
  "In-Person Check In (30 Mins)": 13,
  "Medication Management (15 Mins)": 11,
  "Medication Management (30 Mins)": 9,
  "SGB New Patient Package - 1 Session": 8,
  "Complete Neuro Reset (with Ketamine IV)": 8,
  "Medication Management (60 Mins)": 4,
  "SGB - New Patient Package": 4,
  "Ketamine Therapy Integration Session (optional)": 3,
  "Ketamine IV (Package of 6)": 1,
  "TMS - Single Visit (30 mins)": 1,
  "TMS Accelerated Program": 1,
};

export const HUDSON_MEDICAL_PROCEDURE_POPULARITY = {
  "Medical Consult - Follow Up": 2303,
  "Medical - 15 Minute Follow Up Consultation (Virtual)": 419,
  "Epidural Steriod Injection - Lumbar": 188,
  "Medical Consult": 185,
  "Botox - Cervical Dystonia": 146,
  "Lumbar RFA - Unilateral": 83,
  "Trigger Point Injection - Cervical": 65,
  "Trigger Point Injection - Lumbar": 62,
  "Epidural Steriod Injection - Cervical": 52,
  "Lumbar RFA - Bilateral": 48,
  "Bilateral Visco Knee - Gelsyn": 41,
  "Bilateral Visco Knee - Synvisc": 38,
  "Cervical RFA - Unilateral": 37,
  "Worker's Comp": 36,
  "Outstanding Balance": 36,
  "Unilateral Visco Knee Injection": 26,
  "Bilateral Medial Branch Block - Lumbar": 24,
  "Visco Knee - Gelsyn": 23,
  "Bilateral PRP Back & Spine - Lumbar": 19,
  "Genicular RFA - Unilateral": 18,
  "Cervical RFA - Bilateral": 18,
  "Bilateral Visco Knee - Orthovisc": 17,
  "Unilateral Steroid Injection - Knee": 17,
  "Bilateral Visco Knee - Supartz": 16,
  "Bilateral Medial Branch Block - Cervical": 15,
  "Visco Knee - GenVisc": 14,
  "Unilateral Steroid Injection - Shoulder": 14,
  "Bilateral Visco Knee - GenVisc": 14,
  "Unilateral Steroid Injection - Sacroiliac Joint (Hip)": 14,
  "Ketamine IV": 13,
  "Visco Shoulder - Gelsyn": 11,
  "Bilateral Steroid Injection - Knee": 11,
  "Epidural Steriod Injection - Thoracic": 11,
  "Visco Knee - Synvisc": 11,
  "Medical Marijuana - Standard": 11,
  "Bilateral Visco Hip - Gelsyn": 11,
  "Bilateral Visco Knee - Euflexxa": 10,
  "Bilateral Steroid Injection - Sacroiliac Joint (Hip)": 9,
  "Trigger Point Injection - Thoracic": 9,
  "No Fault": 8,
  "Dysport - Cervical Dystonia": 8,
  "Bilateral Visco Knee - Gel-One": 8,
  "Unilateral PRP Joint & Tendon - LE (Knee)": 7,
  "Visco Shoulder - Orthovisc": 7,
  "Carpal Tunnel Injection": 6,
  "Bilateral Visco Shoulder - Gelsyn": 6,
  "Epidural Steriod Injection - Caudal": 5,
  "Thoracic RFA - Unilateral": 5,
  "Visco Knee - Orthovisc": 5,
  "Unilateral PRP Joint & Tendon - UE (Shoulder)": 4,
  "Toradol Injection": 4,
  "Bilateral PRP Back & Spine - Cervical": 4,
  "Bilateral PRP Joint & Tendon - LE (Knee)": 4,
  "Unilateral Visco Shoulder Injection": 4,
  "Occipital Nerve Block": 4,
  "Bilateral Visco Knee Injection": 4,
  "Bilateral Visco Shoulder - Euflexxa": 4,
  "Auriculotemporal Nerve Block": 4,
  "Botox - Migraines": 4,
  "Bursa Injection": 4,
  "Visco Knee - Euflexxa": 4,
  "Medical Marijuana - For Clients with Medicaid": 4,
  "Unilateral PRP Joint & Tendon - LE (Hip)": 3,
  "Bilateral Genicular Nerve Block": 3,
  "Trigger Injection - Finger": 3,
  "Unilateral Genicular Nerve Block": 3,
  "Visco Knee - Supartz": 3,
  "Unilateral PRP Joint & Tendon - UE (Hand)": 2,
  "Unilateral Steroid Injection - Ankle": 2,
  "Unilateral Steroid Injection - Elbow": 2,
  "Visco Shoulder - GenVisc": 2,
  "Unilateral PRP Back & Spine - Lumbar": 2,
  "Vertebroplasty/Kyphoplasty - Level 1": 2,
  "Medical - 30 Minute Follow Up Consultation (Virtual)": 2,
  "Femoral RFA - Unilateral": 2,
  "Stimular Trial, run by Hudson Health": 2,
  "Steroid Injection - Joint/Tendon": 2,
  "Femoral/Obturator Nerve Block": 2,
  "Thoracic RFA - Bilateral": 2,
  "Bilateral Visco Shoulder - Supartz": 2,
  "Visco Knee - Gel-One": 1,
  "B12 Injection": 1,
  "Bilateral Medial Branch Block - Thoracic": 1,
  "Bilateral PRP Joint & Tendon - LE (Hip)": 1,
  "Visco Shoulder - Gel-One": 1,
  "Bilateral Steroid Injection - Shoulder": 1,
  "Bilateral Visco Hip - GenVisc": 1,
  "Bilateral Visco Hip - Synvisc": 1,
  "Bilateral Visco Shoulder - Orthovisc": 1,
  "Unilateral PRP Back & Spine - Cervical": 1,
  "Cluneal Nerve Block": 1,
  "Immunity Blend IV Infusion": 1,
  "Inguinal Nerve Block": 1,
  "Medical Marijuana - For Clients with Proof of Low Income or Disability": 1,
  "Unilateral Steroid Injection - Plantar Fascia": 1,
};

export const PROCEDURE_POPULARITY = {
  "1": SORIN_PC_PROCEDURE_POPULARITY,
  "3": HUDSON_EXTENSION_PROCEDURE_POPULARITY,
  "6": HUDSON_MEDICAL_PROCEDURE_POPULARITY,
  "7": HUDSON_MIND_PROCEDURE_POPULARITY,
};

export type EventKey =
  | "ACCOUNT_PAYMENT"
  | "CREATE_NEW_APPOINTMENT" // ehrOn: M1, ehrOff: (M7A/M7B)
  | "CREATE_UNSCHEDULED" // ehrOn: M2, ehrOff: M2 - if payment needed, send M1
  | "COMPLETE_PAYMENT_PC"
  | "CANCEL_APPOINTMENT" // ehrOn: nothing, ehrOff: M9A or M9B
  | "EDIT_TIME" // ehrOn: nothing, ehrOff: M8B (or M8A or M8C) - balance dependent
  | "SEND_INTAKE_FORM"
  | "INTAKE_COMPLETE" // --- not sending this anymore
  | "OTP"
  | "REFUND" // ehrOn: M6, ehrOff: M6
  | "RESEND_RECEIPT" // ehrOn: M2, ehrOff: M2
  | "SCHEDULE_UNSCHEDULED" // ehrOn: nothing, ehrOff: M7A or M7B
  | "SEND_CHECK_IN"
  | "STOREFRONT_PAY_AND_SCHEDULE"
  | "STOREFRONT_PAY"
  | "UPSELL" // ehrOn: M5, ehrOff: M5
  | "VERIFY_USER"
  | "WELCOME_MESSAGE"
  | "BALANCE" // not necessarily a balance change, just an existing balance
  | "MIX_BALANCE_CHANGE"; // a generic event for when a mix of balance, refund, and upsell events occur

export type MessageKey =
  | "M0" // welcome message
  | "M0A" // contact info card for phones
  | "M1A"
  | "M1B"
  | "M2"
  | "M3" // send intake form
  | "M4"
  | "M5"
  | "M6"
  | "M7A"
  | "M7B"
  | "M8A"
  | "M8B"
  | "M8C"
  | "M9A"
  | "M9B"
  | "M10A"
  | "M10B"
  | "M_ACCOUNT_PAYMENT"
  | "M_INTAKE_COMPLETE"
  | "M_OTP"
  | "M_RESEND_RECEIPT"
  | "M_SEND_CHECK_IN"
  | "M_VERIFY_USER"
  | "M_STOREFRONT_PAY_AND_SCHEDULE"
  | "M_STOREFRONT_PAY"
  | "M_WELCOME_PREAMBLE"
  | "M_WELCOME_FOOTER"
  | "M_GUARANTOR_PAYMENT";

/*
 * 11/5 HOTFIX - removing granularity on EDIT_TIME, REFUND_EDIT, and UPSELL_EDIT language since they are being sent pre-emptively and/or in incorrect scenarios
 * FIXME: reinterrogate messaging logic
 */
const translatedMessageTemplates: Record<
  MessageKey,
  Partial<{
    [key in LanguageCodeWithFrench]: {
      subject?: (params: MessageParams) => string;
      body: (params: MessageParams) => string;
    };
  }>
> = {
  M0: {
    // welcome message - email
    en: {
      body: ({ orgName }) =>
        `Hello from Superscript! We've partnered with your doctors at ${orgName} to make checking in for your appointment quick and simple. We'll reach out if you owe payment or need to complete any forms before your visit.`,
      subject: () => "Welcome to Superscript!",
    },
    es: {
      body: ({ orgName }) =>
        `¡Saludos desde Superscript! Nos asociamos con sus médicos en ${orgName} para que el registro de su cita sea rápido y sencillo. Nos comunicaremos con usted si tiene alguna pago pendiente o si debe completar algún formulario antes de su visita.`,
      subject: () => "¡Le damos la bienvenida a Superscript!",
    },
    ru: {
      body: ({ orgName }) =>
        `Вас приветствует Superscript! Мы сотрудничаем с вашими врачами в ${orgName}, чтобы сделать запись на прием простой и быстрой. Мы свяжемся с вами, если вам нужно будет оплатить прием или заполнить какие-либо формы перед посещением.`,
      subject: () => "Добро пожаловать в Superscript!",
    },
    zh: {
      body: ({ orgName }) =>
        `来自 Superscript 的问候！我们已与您在 ${orgName} 的医生建立合作关系，以便您快速轻松地办理签到。 如果您有未支付的费用，或需要在就诊前填写任何表格，我们会与您联系。`,
      subject: () => "欢迎使用 Superscript！",
    },
    zh_HK: {
      body: ({ orgName }) =>
        `Superscript 向您問好！我們與您在${orgName}的主治醫師合作，讓您的看診報到流程更加快速且便利。若您在看診前需要繳費或填寫表單，我們將主動通知您。`,
      subject: () => "歡迎使用 Superscript！",
    },
    fr: {
      body: ({ orgName }) =>
        `Bonjour de Superscript ! Nous nous sommes associés à vos médecins de ${orgName} pour rendre votre enregistrement de rendez-vous rapide et simple. Nous vous contacterons si vous devez effectuer un paiement ou remplir des formulaires avant votre visite.`,
      subject: () => "Bienvenue sur Superscript !",
    },
    ht: {
      body: ({ orgName }) =>
        `Bonjou de Superscript! Nou patenè ak doktè ou yo nan ${orgName} pou fè enskripsyon pou randevou ou rapid ak senp. Nou pral kontakte ou si ou dwe peye oswa ranpli fòm anvan vizit ou.`,
      subject: () => "Byenveni nan Superscript!",
    },
  },
  M0A: {
    // contact info card for phones
    en: {
      body: () =>
        "Please take a moment to save our provided contact information. We'll use this number to provide updates on your appointments.",
    },
    es: {
      body: () =>
        "Tómese un momento para guardar la información de contacto que le proporcionamos. Usaremos este número para proporcionarle actualizaciones sobre sus citas.",
    },
    ru: {
      body: () =>
        "Пожалуйста, сохраните предоставленную контактную информацию. Мы будем использовать этот номер, чтобы сообщать вам о ваших приемах.",
    },
    zh: {
      body: () =>
        "请花点时间保存我们提供的联系信息。我们将使用此号码向您提供有关预约的最新信息。",
    },
    zh_HK: {
      body: () =>
        "請花一點時間儲存我們提供的聯絡資訊。我們將使用此電話號碼向您提供預約相關的最新通知。",
    },
    fr: {
      body: () =>
        "Veuillez prendre un moment pour enregistrer les coordonnées que nous vous fournissons. Nous utiliserons ce numéro pour vous tenir informé de vos rendez-vous.",
    },
    ht: {
      body: () =>
        "Tanpri pran yon moman pou sove enfòmasyon kontak nou ba ou a. Nou pral itilize nimewo sa a pou ba ou mizajou sou randevou ou yo.",
    },
  },
  M1A: {
    // payment message - scheduled
    en: {
      body: ({ orgName, appointmentTime, link }) =>
        `Hello from Superscript! It's time to pay for your appointment at ${orgName} on ${appointmentTime}. Click the link below to check out: ${link}`,
    },
    es: {
      body: ({ orgName, appointmentTime, link }) =>
        `¡Saludos desde Superscript! Es hora de pagar su cita en ${orgName} a las ${appointmentTime}. Haga clic en el siguiente enlace para pagar: ${link}`,
    },
    ru: {
      body: ({ orgName, appointmentTime, link }) =>
        `Вас приветствует Superscript! Пора оплатить прием в ${orgName}, назначенный на ${appointmentTime}. Нажмите ссылку ниже, чтобы перейти к оплате: ${link}`,
    },
    zh: {
      body: ({ orgName, appointmentTime, link }) =>
        `来自 Superscript 的问候！请支付您于 ${appointmentTime} 在 ${orgName} 的预约费用。 点击下方链接结账：${link}`,
    },
    zh_HK: {
      body: ({ orgName, appointmentTime, link }) =>
        `Superscript 向您問好！您已預約至${orgName}看診，時間：${appointmentTime}。請點選以下連結完成付款手續：${link}`,
    },
    fr: {
      body: ({ orgName, appointmentTime, link }) =>
        `Bonjour de Superscript ! Il est temps de payer votre rendez-vous chez ${orgName} le ${appointmentTime}. Cliquez sur le lien ci-dessous pour procéder au paiement : ${link}`,
    },
    ht: {
      body: ({ orgName, appointmentTime, link }) =>
        `Bonjou de Superscript! Li lè pou peye pou randevou ou nan ${orgName} nan ${appointmentTime}. Klike sou lyen ki anba a pou peye: ${link}`,
    },
  },
  M1B: {
    // payment message - unscheduled
    en: {
      body: ({ orgName, link }) =>
        `Hello from Superscript! It's time to pay for your treatments at ${orgName}. Click the link below to check out: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Saludos desde Superscript! Es hora de pagar sus tratamientos en ${orgName}. Haga clic en el siguiente enlace para pagar: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Вас приветствует Superscript! Пора оплатить лечение в ${orgName}. Нажмите ссылку ниже, чтобы перейти к оплате: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `来自 Superscript 的问候！请支付您在 ${orgName} 的治疗费用。 点击下方链接结账：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `Superscript 向您問好！請支付您於${orgName}的治療費用。請點選以下連結完成付款手續：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Bonjour de Superscript ! Il est temps de payer vos traitements chez ${orgName}. Cliquez sur le lien ci-dessous pour procéder au paiement : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Bonjou de Superscript! Li lè pou peye pou tretman ou yo nan ${orgName}. Klike sou lyen ki anba a pou peye: ${link}`,
    },
  },
  M2: {
    // receipt message

    en: {
      body: ({ link }) =>
        `Your payment has been received! View your receipt here: ${link}`,
    },
    es: {
      body: ({ link }) =>
        `¡Recibimos su pago! Consulte su recibo aquí: ${link}`,
    },
    ru: {
      body: ({ link }) =>
        `Ваш платеж получен! Просмотреть квитанцию можно здесь: ${link}`,
    },
    zh: { body: ({ link }) => `您的付款已收到！在此处查看您的收据：${link}` },
    zh_HK: {
      body: ({ link }) => `已收到您的付款。在此處查看您的收據：${link}`,
    },
    fr: {
      body: ({ link }) =>
        `Votre paiement a été reçu ! Consultez votre reçu ici : ${link}`,
    },
    ht: {
      body: ({ link }) => `Nou resevwa peman ou! Gade resi ou isit la: ${link}`,
    },
  },
  M3: {
    // intake message
    en: {
      body: ({ orgName, link }) =>
        `Hello! Your doctor at ${orgName} has shared forms they need you to fill out before your visit. Save time in the waiting room, click the link to complete your intake forms online: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Hola! Su médico en ${orgName} compartió formularios que debe completar antes de su visita. Ahorre tiempo en la sala de espera, haga clic en el enlace para completar sus formularios de admisión en línea: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Здравствуйте! Ваш врач из ${orgName} предоставил формы, которые вам нужно заполнить перед визитом. Не тратьте время на ожидание в приемной. Перейдите по ссылке, чтобы заполнить формы онлайн: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `您好！您在 ${orgName} 的医生已分享其需要您在就诊前填写的表格。节省在候诊室的时间，点击链接，在线填写您的初诊登记表：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `您好！您在${orgName}的醫師已傳送需要您於看診前填寫的表單。為節省候診時間，請點擊下方連結，線上完成初診表單填寫：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Bonjour ! Votre médecin chez ${orgName} a partagé des formulaires que vous devez remplir avant votre visite. Gagnez du temps en salle d'attente, cliquez sur le lien pour remplir vos formulaires d'admission en ligne : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Bonjou! Doktè ou nan ${orgName} pataje fòm ou bezwen ranpli anvan vizit ou. Ekonomize tan nan sal datant lan, klike sou lyen an pou ranpli fòm admisyon ou yo sou entènèt: ${link}`,
    },
  },
  M4: {
    // intake reminder message
    en: {
      body: ({ orgName, link }) =>
        `Action Needed! Your doctor at ${orgName} shared forms they need you to fill out before your visit. Click the link to complete your intake forms online: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Acción necesaria! Su médico en ${orgName} compartió formularios que debe completar antes de su visita. Haga clic en el enlace para completar sus formularios de admisión en línea: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Требуется действие! Ваш врач из ${orgName} предоставил формы, которые нужно заполнить перед визитом. Перейдите по ссылке, чтобы заполнить формы онлайн: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `需要采取行动！您在 ${orgName} 的医生已分享其需要您在就诊前填写的表格。点击链接，在线填写您的初诊登记表：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `請立即處理！您在${orgName}的醫師已傳送需要您於看診前填寫的表單。請點擊下方連結，線上完成初診表單填寫：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Action requise ! Votre médecin chez ${orgName} a partagé des formulaires que vous devez remplir avant votre visite. Cliquez sur le lien pour remplir vos formulaires d'admission en ligne : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Aksyon Obligatwa! Doktè ou nan ${orgName} pataje fòm ou bezwen ranpli anvan vizit ou. Klike sou lyen an pou ranpli fòm admisyon ou yo sou entènèt: ${link}`,
    },
  },
  M5: {
    // additional charge message
    en: {
      body: ({ orgName, link }) =>
        `Hello from Superscript! Your visit at ${orgName} has been updated to reflect an additional charge. Pay for your additional amount: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Saludos desde Superscript! Su visita a ${orgName} se actualizó para reflejar un cargo adicional. Pague por su cantidad adicional: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Вас приветствует Superscript! Информация о вашем визите в ${orgName} обновлена с учетом дополнительной оплаты. Оплатите свою дополнительную сумму: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `来自 Superscript 的问候！您在 ${orgName} 的就诊已更新，以反映额外费用。 点击下方链接，支付您的额外费用：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `Superscript 向您問好！您在${orgName}的看診記錄已更新，新增了一筆費用。請點擊下方連結，完成付款：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Bonjour de Superscript ! Votre visite chez ${orgName} a été mise à jour pour refléter des frais supplémentaires. Payez le montant supplémentaire : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Bonjou de Superscript! Vizit ou nan ${orgName} te mete ajou pou reflete yon chaj siplemantè. Peye montan siplemantè ou: ${link}`,
    },
  },
  M6: {
    // refund message
    en: {
      body: ({ orgName, link }) =>
        `Hello from Superscript! You have been issued a refund for your recent visit at ${orgName}. View your receipt here: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Saludos desde Superscript! Emitimos un reembolso para usted por su reciente visita a ${orgName}. Consulte su recibo aquí: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Вас приветствует Superscript! Вам возвращена оплата за недавний визит в ${orgName}. Просмотреть квитанцию можно здесь: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `来自 Superscript 的问候！您最近在 ${orgName} 的就诊已获得退款。在此处查看您的收据：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `Superscript 向您問好！您在${orgName}最近一次就診已獲退款。在此處查看您的收據：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Bonjour de Superscript ! Un remboursement a été émis pour votre récente visite chez ${orgName}. Consultez votre reçu ici : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Bonjou de Superscript! Yo te fè yon ranbousman pou dènye vizit ou nan ${orgName}. Gade resi ou isit la: ${link}`,
    },
  },
  M7A: {
    // schedule with balance
    en: {
      body: ({ orgName, appointmentTime, link }) =>
        `Your appointment at ${orgName} has been scheduled for ${appointmentTime}. Click the link below to pay for your visit: ${link}`,
    },
    es: {
      body: ({ orgName, appointmentTime, link }) =>
        `Su cita en ${orgName} se programó para las ${appointmentTime}. Haga clic en el siguiente enlace para pagar su visita: ${link}`,
    },
    ru: {
      body: ({ orgName, appointmentTime, link }) =>
        `Ваш прием в ${orgName} запланирован на ${appointmentTime}. Нажмите ссылку ниже, чтобы оплатить визит: ${link}`,
    },
    zh: {
      body: ({ orgName, appointmentTime, link }) =>
        `您在 ${orgName} 的预约已安排在 ${appointmentTime}。 点击下方链接，支付您的就诊费用：${link}`,
    },
    zh_HK: {
      body: ({ orgName, appointmentTime, link }) =>
        `您在${orgName}的看診已安排於 ${appointmentTime}。請點選以下連結，完成付款：${link}`,
    },
    fr: {
      body: ({ orgName, appointmentTime, link }) =>
        `Votre rendez-vous chez ${orgName} a été programmé pour le ${appointmentTime}. Cliquez sur le lien ci-dessous pour payer votre visite : ${link}`,
    },
    ht: {
      body: ({ orgName, appointmentTime, link }) =>
        `Randevou ou nan ${orgName} pwograme pou ${appointmentTime}. Klike sou lyen ki anba a pou peye pou vizit ou: ${link}`,
    },
  },
  M7B: {
    // schedule without balance
    en: {
      body: ({ orgName, appointmentTime, link }) =>
        `Hello from Superscript! Your appointment at ${orgName} has been scheduled for ${appointmentTime}. Click the link below to view your upcoming visit: ${link}`,
    },
    es: {
      body: ({ orgName, appointmentTime, link }) =>
        `¡Saludos desde Superscript! Su cita en ${orgName} se programó para las ${appointmentTime}. Haga clic en el siguiente enlace para ver su próxima visita: ${link}`,
    },
    ru: {
      body: ({ orgName, appointmentTime, link }) =>
        `Вас приветствует Superscript! Ваш прием в ${orgName} запланирован на ${appointmentTime}. Нажмите на ссылку ниже, чтобы посмотреть ваш предстоящий визит: ${link}`,
    },
    zh: {
      body: ({ orgName, appointmentTime, link }) =>
        `来自 Superscript 的问候！您在 ${orgName} 的预约已安排在 ${appointmentTime}。点击下方链接，查看您的就诊信息：${link}`,
    },
    zh_HK: {
      body: ({ orgName, appointmentTime, link }) =>
        `Superscript 向您問好！您在${orgName}的看診已安排於 ${appointmentTime}。請點選以下連結，查看您的就診資訊：${link}`,
    },
    fr: {
      body: ({ orgName, appointmentTime, link }) =>
        `Bonjour de Superscript ! Votre rendez-vous chez ${orgName} a été programmé pour le ${appointmentTime}. Cliquez sur le lien ci-dessous pour voir votre visite à venir : ${link}`,
    },
    ht: {
      body: ({ orgName, appointmentTime, link }) =>
        `Bonjou de Superscript! Randevou ou nan ${orgName} pwograme pou ${appointmentTime}. Klike sou lyen ki anba a pou wè vizit pwochèn ou: ${link}`,
    },
  },
  M8A: {
    // re-schedule with balance
    en: {
      body: ({ orgName, appointmentTime, link }) =>
        `Hello from Superscript! Your appointment at ${orgName} has been re-scheduled for ${appointmentTime}. Click the link below to pay for your visit: ${link}`,
    },
    es: {
      body: ({ orgName, appointmentTime, link }) =>
        `¡Saludos desde Superscript! Su cita en ${orgName} se reprogramó para las ${appointmentTime}. Haga clic en el siguiente enlace para pagar su visita: ${link}`,
    },
    ru: {
      body: ({ orgName, appointmentTime, link }) =>
        `Вас приветствует Superscript! Ваш прием в ${orgName} перенесен на ${appointmentTime}. Нажмите ссылку ниже, чтобы оплатить визит: ${link}`,
    },
    zh: {
      body: ({ orgName, appointmentTime, link }) =>
        `来自 Superscript 的问候！您在 ${orgName} 的预约时间已改为 ${appointmentTime}。点击下方链接，支付您的就诊费用：${link}`,
    },
    zh_HK: {
      body: ({ orgName, appointmentTime, link }) =>
        `Superscript 向您問好！您在${orgName}的看診已重新安排於 ${appointmentTime}。請點選以下連結，完成付款：${link}`,
    },
    fr: {
      body: ({ orgName, appointmentTime, link }) =>
        `Bonjour de Superscript ! Votre rendez-vous chez ${orgName} a été reprogrammé pour le ${appointmentTime}. Cliquez sur le lien ci-dessous pour payer votre visite : ${link}`,
    },
    ht: {
      body: ({ orgName, appointmentTime, link }) =>
        `Bonjou de Superscript! Randevou ou nan ${orgName} repwograme pou ${appointmentTime}. Klike sou lyen ki anba a pou peye pou vizit ou: ${link}`,
    },
  },
  M8B: {
    // re-schedule no balance
    en: {
      body: ({ orgName, appointmentTime }) =>
        `Hello from Superscript! Your appointment at ${orgName} has been re-scheduled for ${appointmentTime}.`,
    },
    es: {
      body: ({ orgName, appointmentTime }) =>
        `¡Saludos desde Superscript! Su cita en ${orgName} se reprogramó para las ${appointmentTime}.`,
    },
    ru: {
      body: ({ orgName, appointmentTime }) =>
        `Вас приветствует Superscript! Ваш прием в ${orgName} перенесен на ${appointmentTime}.`,
    },
    zh: {
      body: ({ orgName, appointmentTime }) =>
        `来自 Superscript 的问候！您在 ${orgName} 的预约时间已改为 ${appointmentTime}。`,
    },
    zh_HK: {
      body: ({ orgName, appointmentTime }) =>
        `Superscript 向您問好！您在${orgName}的看診已重新安排於 ${appointmentTime}。`,
    },
    fr: {
      body: ({ orgName, appointmentTime }) =>
        `Bonjour de Superscript ! Votre rendez-vous chez ${orgName} a été reprogrammé pour le ${appointmentTime}.`,
    },
    ht: {
      body: ({ orgName, appointmentTime }) =>
        `Bonjou de Superscript! Randevou ou nan ${orgName} repwograme pou ${appointmentTime}.`,
    },
  },
  M8C: {
    // re-schedule with refund
    en: {
      body: ({ orgName, appointmentTime, link }) =>
        `Your appointment at ${orgName} has been re-scheduled for ${appointmentTime}. A refund has been issued to reflect the new cost of your visit. View your receipt here: ${link}`,
    },
    es: {
      body: ({ orgName, appointmentTime, link }) =>
        `Su cita en ${orgName} se reprogramó para las ${appointmentTime}. Se emitió un reembolso para reflejar el nuevo costo de su visita. Consulte su recibo aquí: ${link}`,
    },
    ru: {
      body: ({ orgName, appointmentTime, link }) =>
        `Ваш прием в ${orgName} перенесен на ${appointmentTime}. Мы произвели возврат средств с учетом новой стоимости визита. Просмотреть квитанцию можно здесь: ${link}`,
    },
    zh: {
      body: ({ orgName, appointmentTime, link }) =>
        `您在 ${orgName} 的预约时间已改为 ${appointmentTime}。已发放退款，以反映您的新就诊费用。在此处查看您的收据：${link}`,
    },
    zh_HK: {
      body: ({ orgName, appointmentTime, link }) =>
        `您在${orgName}的看診已重新安排於 ${appointmentTime}。因應您本次看診費用之調整，已核發相應退款。在此處查看您的收據：${link}`,
    },
    fr: {
      body: ({ orgName, appointmentTime, link }) =>
        `Votre rendez-vous chez ${orgName} a été reprogrammé pour le ${appointmentTime}. Un remboursement a été émis pour refléter le nouveau coût de votre visite. Consultez votre reçu ici : ${link}`,
    },
    ht: {
      body: ({ orgName, appointmentTime, link }) =>
        `Randevou ou nan ${orgName} repwograme pou ${appointmentTime}. Yo te fè yon ranbousman pou reflete nouvo pri vizit ou. Gade resi ou isit la: ${link}`,
    },
  },
  M9A: {
    // cancellation without refund
    en: {
      body: ({ orgName, appointmentTime }) =>
        `Hello from Superscript! Your appointment at ${orgName} on ${appointmentTime} has been cancelled.`,
    },
    es: {
      body: ({ orgName, appointmentTime }) =>
        `¡Saludos desde Superscript! Su cita en ${orgName} a las ${appointmentTime} se canceló.`,
    },
    ru: {
      body: ({ orgName, appointmentTime }) =>
        `Вас приветствует Superscript! Ваш прием в ${orgName} на ${appointmentTime} отменен.`,
    },
    zh: {
      body: ({ orgName, appointmentTime }) =>
        `来自 Superscript 的问候！您在 ${orgName} 的预约时间 ${appointmentTime} 已取消。`,
    },
    zh_HK: {
      body: ({ orgName, appointmentTime }) =>
        `Superscript 向您問好！您原訂於 ${appointmentTime} 在${orgName}的看診已被取消。`,
    },
    fr: {
      body: ({ orgName, appointmentTime }) =>
        `Bonjour de Superscript ! Votre rendez-vous chez ${orgName} le ${appointmentTime} a été annulé.`,
    },
    ht: {
      body: ({ orgName, appointmentTime }) =>
        `Bonjou de Superscript! Randevou ou nan ${orgName} nan ${appointmentTime} te anile.`,
    },
  },
  M9B: {
    // cancellation with refund
    en: {
      body: ({ orgName, appointmentTime, link }) =>
        `Hello from Superscript! Your appointment at ${orgName} on ${appointmentTime} has been cancelled and you have been refunded. View your receipt here: ${link}`,
    },
    es: {
      body: ({ orgName, appointmentTime, link }) =>
        `¡Saludos desde Superscript! Su cita en ${orgName} a las ${appointmentTime} se canceló y se le reembolsó. Consulte su recibo aquí: ${link}`,
    },
    ru: {
      body: ({ orgName, appointmentTime, link }) =>
        `Вас приветствует Superscript! Ваш прием в ${orgName} на ${appointmentTime} отменен, и вам вернули деньги. Просмотреть квитанцию можно здесь: ${link}`,
    },
    zh: {
      body: ({ orgName, appointmentTime, link }) =>
        `来自 Superscript 的问候！您在 ${orgName} 的预约时间 ${appointmentTime} 已取消，我们已向您退款。 在此处查看您的收据：${link}`,
    },
    zh_HK: {
      body: ({ orgName, appointmentTime, link }) =>
        `Superscript 向您問好！您原訂於 ${appointmentTime} 在${orgName}的看診已被取消，並已為您辦理退款。在此處查看您的收據：${link}`,
    },
    fr: {
      body: ({ orgName, appointmentTime, link }) =>
        `Bonjour de Superscript ! Votre rendez-vous chez ${orgName} le ${appointmentTime} a été annulé et vous avez été remboursé. Consultez votre reçu ici : ${link}`,
    },
    ht: {
      body: ({ orgName, appointmentTime, link }) =>
        `Bonjou de Superscript! Randevou ou nan ${orgName} nan ${appointmentTime} te anile epi yo ranbouse ou. Gade resi ou isit la: ${link}`,
    },
  },
  M10A: {
    // generic mix balance change, no time change
    en: {
      body: ({ orgName, link }) =>
        `Hello from Superscript! There have been updates to your appointment at ${orgName}. Please click the link below to pay your updated balance: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Saludos desde Superscript! Hay actualizaciones sobre su cita en ${orgName}. Haga clic en el siguiente enlace para pagar su saldo actualizado: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Вас приветствует Superscript! В вашем приеме в ${orgName} произошли изменения. Нажмите ссылку ниже, чтобы оплатить обновленный счет: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `来自 Superscript 的问候！您在 ${orgName} 的预约已更新。 请点击下方链接，支付更新后的余额：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `Superscript 向您問好！您在${orgName}的預約已有更新。請點擊以下連結，以支付更新後的應付款項：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Bonjour de Superscript ! Des mises à jour ont été apportées à votre rendez-vous chez ${orgName}. Veuillez cliquer sur le lien ci-dessous pour payer votre solde mis à jour : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Bonjou de Superscript! Te gen mizajou pou randevou ou nan ${orgName}. Tanpri klike sou lyen ki anba a pou peye balans mizajou ou: ${link}`,
    },
  },
  M10B: {
    // generic mix balance change, with time change
    en: {
      body: ({ orgName, link, appointmentTime }) =>
        `Hello from Superscript! Your appointment at ${orgName} has been re-scheduled for ${appointmentTime}, and there have been updates to your balance. Please click the link below to pay: ${link}`,
    },
    es: {
      body: ({ orgName, link, appointmentTime }) =>
        `¡Saludos desde Superscript! Su cita en ${orgName} se reprogramó para las ${appointmentTime}, y hay actualizaciones sobre su saldo. Haga clic en el siguiente enlace para pagar: ${link}`,
    },
    ru: {
      body: ({ orgName, link, appointmentTime }) =>
        `Вас приветствует Superscript! Ваш прием в ${orgName} перенесен на ${appointmentTime}, и ваш счет обновлен. Нажмите ссылку ниже, чтобы оплатить: ${link}`,
    },
    zh: {
      body: ({ orgName, link, appointmentTime }) =>
        `来自 Superscript 的问候！您在 ${orgName} 的预约时间已改为 ${appointmentTime}，您的余额也已更新。请点击下方链接完成支付：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link, appointmentTime }) =>
        `Superscript 向您問好！您在${orgName}的預約已重新安排至 ${appointmentTime}，同時您的應付款項亦有更新。請點擊以下連結進行付款：${link}`,
    },
    fr: {
      body: ({ orgName, link, appointmentTime }) =>
        `Bonjour de Superscript ! Votre rendez-vous chez ${orgName} a été reprogrammé pour le ${appointmentTime}, et votre solde a été mis à jour. Veuillez cliquer sur le lien ci-dessous pour payer : ${link}`,
    },
    ht: {
      body: ({ orgName, link, appointmentTime }) =>
        `Bonjou de Superscript! Randevou ou nan ${orgName} repwograme pou ${appointmentTime}, epi balans ou te mete ajou. Tanpri klike sou lyen ki anba a pou peye: ${link}`,
    },
  },
  M_ACCOUNT_PAYMENT: {
    en: {
      body: ({ orgName, link }) =>
        `Thank you for checking out with Superscript! Please see your attached ${orgName} receipt here: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Gracias por pagar con Superscript! Consulte su recibo adjunto de ${orgName} aquí: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Благодарим за выбор Superscript! Просмотреть квитанцию от ${orgName} можно здесь: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `感谢您使用 Superscript 结账！ 请查看此处附上的 ${orgName} 收据：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `感謝您透過 Superscript 完成付款！請點選以下連結，查看您在${orgName}的收據：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Merci d'avoir effectué votre paiement avec Superscript ! Veuillez consulter votre reçu ${orgName} ci-joint : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Mèsi pou peye ak Superscript! Tanpri gade resi ${orgName} ou ki atache isit la: ${link}`,
    },
  },
  M_INTAKE_COMPLETE: {
    en: {
      body: () =>
        "Thank you for checking in with Superscript! The doctor will be ready to see you when you show up for your appointment.",
      subject: () => "You've completed your check-in through Superscript!",
    },
    es: {
      body: () =>
        "¡Gracias por registrarse con Superscript! El médico tendrá todo listo para verle cuando se presente a su cita.",
      subject: () => "¡Ha completado su registro a través de Superscript!",
    },
    ru: {
      body: () =>
        "Благодарим за выбор Superscript! Врач будет готов принять вас, когда вы придете на прием.",
      subject: () => "Вы зарегистрировались в Superscript!",
    },
    zh: {
      body: () =>
        "感谢您使用 Superscript 签到！当您到达诊所时，医生将准备好接待您。",
      subject: () => "您已通过 Superscript 完成签到！",
    },
    zh_HK: {
      body: () =>
        "感謝您透過 Superscript 完成報到！您抵達後，醫師將準備為您看診。",
      subject: () => "您已成功透過 Superscript 完成報到！",
    },
    fr: {
      body: () =>
        "Merci de vous être enregistré avec Superscript ! Le médecin sera prêt à vous voir lorsque vous arriverez pour votre rendez-vous.",
      subject: () => "Vous avez terminé votre enregistrement via Superscript !",
    },
    ht: {
      body: () =>
        "Mèsi pou tcheke ak Superscript! Doktè a pral pare pou wè ou lè ou rive pou randevou ou.",
      subject: () => "Ou fin tcheke via Superscript!",
    },
  },
  M_OTP: {
    en: {
      body: ({ orgName, otp }) =>
        `Your One-Time Password from ${orgName} is: ${otp}. Don't share this code with anyone; our employees will never ask for this code.`,
      subject: ({ orgName }) => `One-Time Password from ${orgName}`,
    },
    es: {
      body: ({ orgName, otp }) =>
        `Su contraseña de un solo uso de ${orgName} es: ${otp}. No comparta este código con nadie. Nuestro personal nunca se lo pedirá.`,
      subject: ({ orgName }) => `Contraseña de un solo uso de ${orgName}`,
    },
    ru: {
      body: ({ orgName, otp }) =>
        `Ваш одноразовый пароль для ${orgName}: ${otp}. Никому не сообщайте этот код. Наши сотрудники никогда его не спрашивают.`,
      subject: ({ orgName }) => `Одноразовый пароль для ${orgName}`,
    },
    zh: {
      body: ({ orgName, otp }) =>
        `您的来自 ${orgName} 的一次性密码是：${otp}。 请勿与任何人分享此密码；我们的员工绝不会要求您提供此密码。`,
      subject: ({ orgName }) => `来自 ${orgName} 的一次性密码`,
    },
    zh_HK: {
      body: ({ orgName, otp }) =>
        `您來自${orgName}的一次性驗證碼為：${otp}。請勿與他人分享此驗證碼，我們的工作人員絕不會主動向您索取此代碼。`,
      subject: ({ orgName }) => `來自${orgName}的一次性驗證碼`,
    },
    fr: {
      body: ({ orgName, otp }) =>
        `Votre mot de passe à usage unique de ${orgName} est : ${otp}. Ne partagez ce code avec personne ; nos employés ne vous demanderont jamais ce code.`,
      subject: ({ orgName }) => `Mot de passe à usage unique de ${orgName}`,
    },
    ht: {
      body: ({ orgName, otp }) =>
        `Modpas Yon Sèl Fwa ou nan ${orgName} se: ${otp}. Pa pataje kòd sa a ak pèsòn; anplwaye nou yo pa janm mande kòd sa a.`,
      subject: ({ orgName }) => `Modpas Yon Sèl Fwa nan ${orgName}`,
    },
  },
  M_RESEND_RECEIPT: {
    en: {
      body: ({ orgName, link }) =>
        `Hello from Superscript! Please see your attached ${orgName} receipt here: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Saludos desde Superscript! Consulte su recibo adjunto de ${orgName} aquí: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Вас приветствует Superscript! Просмотреть квитанцию от ${orgName} можно здесь: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `来自 Superscript 的问候！请查看此处附上的 ${orgName} 收据：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `Superscript 向您問好！請點選以下連結，查看您在${orgName}的收據：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Bonjour de Superscript ! Veuillez consulter votre reçu ${orgName} ci-joint : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Bonjou de Superscript! Tanpri gade resi ${orgName} ou ki atache isit la: ${link}`,
    },
  },
  M_SEND_CHECK_IN: {
    en: {
      body: ({ orgName, link }) =>
        `It's time to check in! Skip the waiting room!\n\nCheck in for your upcoming visit at ${orgName} here: ${link}`,
    },
    es: {
      body: ({ orgName, link }) =>
        `¡Es hora de registrarse! ¡Evite la sala de espera!\n\nRegístrese para su próxima visita a ${orgName} aquí: ${link}`,
    },
    ru: {
      body: ({ orgName, link }) =>
        `Пора записаться на прием! Не тратьте время на ожидание в приемной!\n\nЗапланируйте предстоящий визит в ${orgName} здесь: ${link}`,
    },
    zh: {
      body: ({ orgName, link }) =>
        `是时候签到了！ 无需在候诊室等候！\n\n在此处为您在 ${orgName} 即将到来的就诊签到：${link}`,
    },
    zh_HK: {
      body: ({ orgName, link }) =>
        `該報到了！免去候診等待！\n\n點擊以下連結，為您即將在${orgName}的看診完成線上報到：${link}`,
    },
    fr: {
      body: ({ orgName, link }) =>
        `Il est temps de vous enregistrer ! Évitez la salle d'attente !\n\nEnregistrez-vous pour votre prochaine visite chez ${orgName} ici : ${link}`,
    },
    ht: {
      body: ({ orgName, link }) =>
        `Li lè pou tcheke! Evite sal datant lan!\n\nTcheke pou vizit pwochèn ou nan ${orgName} isit la: ${link}`,
    },
  },
  M_STOREFRONT_PAY_AND_SCHEDULE: {
    en: {
      body: ({ orgName, appointmentTime, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Your appointment at ${orgName} has been scheduled for ${appointmentTime}.\n\nPlease see your attached ${orgName} receipt here: ${link}`
          : `Thank you for checking out with Superscript! Your appointment at ${orgName} has been scheduled for ${appointmentTime}.\n\nPlease see your attached ${orgName} receipt here: ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "Welcome to Superscript!"
          : "Thank you for checking out with Superscript!",
    },
    es: {
      body: ({ orgName, appointmentTime, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Su cita en ${orgName} se programó para las ${appointmentTime}.\n\nPuede consultar su comprobante de ${orgName} aquí: ${link}`
          : `¡Gracias por pagar con Superscript! Su cita en ${orgName} se programó para las ${appointmentTime}.\n\nPuede consultar su comprobante de ${orgName} aquí: ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "¡Le damos la bienvenida a Superscript!"
          : "¡Gracias por pagar con Superscript!",
    },
    ru: {
      body: ({ orgName, appointmentTime, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Ваш прием в ${orgName} был назначен на ${appointmentTime}.\n\nОзнакомьтесь с приложенной квитанцией ${orgName} здесь: ${link}`
          : `Благодарим за выбор Superscript! Ваш прием в ${orgName} был назначен на ${appointmentTime}.\n\nОзнакомьтесь с приложенной квитанцией ${orgName} здесь: ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "Добро пожаловать в Superscript!"
          : "Благодарим за выбор Superscript!",
    },
    zh: {
      body: ({ orgName, appointmentTime, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `您在 ${orgName} 的预约已安排在 ${appointmentTime}。\n\n请查看此处附上的 ${orgName} 收据：${link}`
          : `感谢您使用 Superscript 结账！ 您在 ${orgName} 的预约已安排在 ${appointmentTime}。\n\n请查看此处附上的 ${orgName} 收据：${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "欢迎使用 Superscript！"
          : "感谢您使用 Superscript 结账！",
    },
    zh_HK: {
      body: ({ orgName, appointmentTime, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `您在${orgName}的預約已安排於 ${appointmentTime}。\n\n請點選以下連結，查看您在${orgName}的收據：${link}`
          : `感謝您透過 Superscript 完成付款！您在${orgName}的預約已安排於 ${appointmentTime}。\n\n請點選以下連結，查看您在${orgName}的收據：${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage ? "歡迎使用 Superscript！" : "感謝你的更新！",
    },
    fr: {
      body: ({ orgName, appointmentTime, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Votre rendez-vous chez ${orgName} a été programmé pour le ${appointmentTime}.\n\nVeuillez consulter votre reçu ${orgName} ci-joint : ${link}`
          : `Merci d'avoir effectué votre paiement avec Superscript ! Votre rendez-vous chez ${orgName} a été programmé pour le ${appointmentTime}.\n\nVeuillez consulter votre reçu ${orgName} ci-joint : ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "Bienvenue sur Superscript !"
          : "Merci d'avoir effectué votre paiement avec Superscript !",
    },
    ht: {
      body: ({ orgName, appointmentTime, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Randevou ou nan ${orgName} pwograme pou ${appointmentTime}.\n\nTanpri gade resi ${orgName} ou ki atache isit la: ${link}`
          : `Mèsi pou peye ak Superscript! Randevou ou nan ${orgName} pwograme pou ${appointmentTime}.\n\nTanpri gade resi ${orgName} ou ki atache isit la: ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "Byenveni nan Superscript!"
          : "Mèsi pou peye ak Superscript!",
    },
  },
  M_STOREFRONT_PAY: {
    en: {
      body: ({ orgName, phoneNumber, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `An admin from ${orgName} will call you to schedule your visit, or you can reach out to them at ${phoneNumber}.\n\nPlease see your attached ${orgName} receipt here: ${link}`
          : `Thank you for checking out with Superscript! An admin from ${orgName} will call you to schedule your visit, or you can reach out to them at ${phoneNumber}.\n\nPlease see your attached ${orgName} receipt here: ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "Welcome to Superscript!"
          : "Thank you for checking out with Superscript!",
    },
    es: {
      body: ({ orgName, phoneNumber, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Un administrador de ${orgName} le llamará para programar su visita o puede comunicarse con ellos al ${phoneNumber}.\n\nPuede consultar su comprobante de ${orgName} aquí: ${link}`
          : `¡Gracias por pagar con Superscript! Un administrador de ${orgName} le llamará para programar su visita o puede comunicarse con ellos al ${phoneNumber}.\n\nPuede consultar su comprobante de ${orgName} aquí: ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "¡Le damos la bienvenida a Superscript!"
          : "¡Gracias por pagar con Superscript!",
    },
    ru: {
      body: ({ orgName, phoneNumber, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Администратор ${orgName} позвонит вам, чтобы запланировать визит, или вы можете связаться с ними по телефону ${phoneNumber}.\n\nОзнакомьтесь с приложенной квитанцией ${orgName} здесь: ${link}`
          : `Благодарим за выбор Superscript! Администратор ${orgName} позвонит вам, чтобы запланировать визит, или вы можете связаться с ними по телефону ${phoneNumber}.\n\nОзнакомьтесь с приложенной квитанцией ${orgName} здесь: ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "Добро пожаловать в Superscript!"
          : "Благодарим за выбор Superscript!",
    },
    zh: {
      body: ({ orgName, phoneNumber, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `一位 ${orgName} 的行政人员将向您致电以安排您的就诊，或者您可以通过 ${phoneNumber} 联系他们。\n\n请查看此处附上的 ${orgName} 收据：${link}`
          : `感谢您使用 Superscript 结账！ 一位 ${orgName} 的行政人员将向您致电以安排您的就诊，或者您可以通过 ${phoneNumber} 联系他们。\n\n请查看此处附上的 ${orgName} 收据：${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "欢迎使用 Superscript！"
          : "感谢您使用 Superscript 结账！",
    },
    zh_HK: {
      body: ({ orgName, phoneNumber, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `${orgName}的行政人員將致電與您安排看診時間，您也可主動致電 ${phoneNumber} 聯繫他們。\n\n請點選以下連結，查看您在${orgName}的收據：${link}`
          : `感謝您透過 Superscript 完成付款！${orgName}的行政人員將致電與您安排看診時間，您也可主動致電 ${phoneNumber} 聯繫他們。\n\n請點選以下連結，查看您在${orgName}的收據：${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage ? "歡迎使用 Superscript！" : "感謝你的更新！",
    },
    fr: {
      body: ({ orgName, phoneNumber, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Un administrateur de ${orgName} vous appellera pour planifier votre visite, ou vous pouvez les contacter au ${phoneNumber}.\n\nVeuillez consulter votre reçu ${orgName} ci-joint : ${link}`
          : `Merci d'avoir effectué votre paiement avec Superscript ! Un administrateur de ${orgName} vous appellera pour planifier votre visite, ou vous pouvez les contacter au ${phoneNumber}.\n\nVeuillez consulter votre reçu ${orgName} ci-joint : ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "Bienvenue sur Superscript !"
          : "Merci d'avoir effectué votre paiement avec Superscript !",
    },
    ht: {
      body: ({ orgName, phoneNumber, link, sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? `Yon administratè nan ${orgName} pral rele ou pou pwograme vizit ou, oswa ou ka kontakte yo nan ${phoneNumber}.\n\nTanpri gade resi ${orgName} ou ki atache isit la: ${link}`
          : `Mèsi pou peye ak Superscript! Yon administratè nan ${orgName} pral rele ou pou pwograme vizit ou, oswa ou ka kontakte yo nan ${phoneNumber}.\n\nTanpri gade resi ${orgName} ou ki atache isit la: ${link}`,
      subject: ({ sendWelcomeMessage }) =>
        sendWelcomeMessage
          ? "Byenveni nan Superscript!"
          : "Mèsi pou peye ak Superscript!",
    },
  },
  M_VERIFY_USER: {
    en: {
      body: ({ verificationUrl }) =>
        `Hello from Superscript! Thanks for checking out with us. To verify your new account, click the link below. This link will expire after 7 days. Contact support@superscript.nyc for questions regarding this payment.\n\n${verificationUrl}`,
      subject: () => "Verify your Superscript account",
    },
    es: {
      body: ({ verificationUrl }) =>
        `¡Saludos desde Superscript! Gracias por elegirnos para pagar. Para verificar su cuenta nueva, haga clic en el siguiente enlace. Este enlace vencerá después de 7 días. Contacte a support@superscript.nyc si tiene preguntas sobre este pago.\n\n${verificationUrl}`,
      subject: () => "Verifique su cuenta de Superscript.",
    },
    ru: {
      body: ({ verificationUrl }) =>
        `Вас приветствует Superscript! Спасибо, что обратились к нам. Чтобы подтвердить свой новый аккаунт, перейдите по ссылке ниже. Срок действия этой ссылки истекает через 7 дней. При возникновении вопросов об этом платеже обращайтесь по адресу support@superscript.nyc.\n\n${verificationUrl}`,
      subject: () => "Подтвердите свой аккаунт Superscript",
    },
    zh: {
      body: ({ verificationUrl }) =>
        `来自 Superscript 的问候！感谢您使用我们的服务结账。要验证您的新帐户，请点击下方链接。此链接将在 7 天后过期。如有关于此付款的问题，请联系 support@superscript.nyc。\n\n${verificationUrl}`,
      subject: () => "验证您的 Superscript 账户",
    },
    zh_HK: {
      body: ({ verificationUrl }) =>
        `Superscript 向您問好！感謝您使用本平台完成付款。請點擊下方連結，以驗證您的新帳戶。此連結將於 7 天後失效。若您對本次付款有任何疑問，請與 support@superscript.nyc 聯絡。\n\n${verificationUrl}`,
      subject: () => "驗證您的 Superscript 帳戶",
    },
    fr: {
      body: ({ verificationUrl }) =>
        `Bonjour de Superscript ! Merci d'avoir effectué votre paiement avec nous. Pour vérifier votre nouveau compte, cliquez sur le lien ci-dessous. Ce lien expirera après 7 jours. Contactez support@superscript.nyc pour toute question concernant ce paiement.\n\n${verificationUrl}`,
      subject: () => "Vérifiez votre compte Superscript",
    },
    ht: {
      body: ({ verificationUrl }) =>
        `Bonjou de Superscript! Mèsi pou peye ak nou. Pou verifye nouvo kont ou, klike sou lyen ki anba a. Lyen sa a pral ekspire apre 7 jou. Kontakte support@superscript.nyc pou kesyon konsènan peman sa a.\n\n${verificationUrl}`,
      subject: () => "Verifye kont Superscript ou",
    },
  },
  M_WELCOME_PREAMBLE: {
    en: {
      body: ({ orgName }) =>
        `Hello from Superscript! We've partnered with your doctors at ${orgName} to make paying and checking in for your appointment quick and simple. Thanks for checking out with us.`,
    },
    es: {
      body: ({ orgName }) =>
        `¡Saludos desde Superscript! Nos asociamos con sus médicos en ${orgName} para que el pago y el registro de su cita sean rápidos y sencillos. Gracias por elegirnos para pagar.`,
    },
    ru: {
      body: ({ orgName }) =>
        `Вас приветствует Superscript! Мы сотрудничаем с вашими врачами из ${orgName}, чтобы сделать запись на прием простой и быстрой. Спасибо, что обратились к нам.`,
    },
    zh: {
      body: ({ orgName }) =>
        `来自 Superscript 的问候！我们已与您在 ${orgName} 的医生建立合作关系，以便您快速轻松地办理支付和签到。感谢您使用我们的服务结账。`,
    },
    zh_HK: {
      body: ({ orgName }) =>
        `Superscript 向您問好！我們已與您在${orgName}的醫師合作，讓您能更快速、輕鬆地完成預約付款與報到手續。感謝您使用本平台完成付款。`,
    },
    fr: {
      body: ({ orgName }) =>
        `Bonjour de Superscript ! Nous nous sommes associés à vos médecins chez ${orgName} pour rendre le paiement et l'enregistrement de votre rendez-vous rapides et simples. Merci d'avoir effectué votre paiement avec nous.`,
    },
    ht: {
      body: ({ orgName }) =>
        `Bonjou de Superscript! Nou patenè ak doktè ou yo nan ${orgName} pou fè peman ak enskripsyon pou randevou ou rapid ak senp. Mèsi pou peye ak nou.`,
    },
  },
  M_WELCOME_FOOTER: {
    en: {
      body: ({ useEmailSignoff }) =>
        useEmailSignoff
          ? "If you have any questions, please reach out to us at support@superscript.nyc.\n\nLove,\nSuperscript"
          : "If you have any questions, please reach out to us at support@superscript.nyc.",
    },
    es: {
      body: ({ useEmailSignoff }) =>
        useEmailSignoff
          ? "Si tiene alguna pregunta, contáctenos a support@superscript.nyc.\n\nSaludos,\nSuperscript"
          : "Si tiene alguna pregunta, escriba a support@superscript.nyc.",
    },
    ru: {
      body: ({ useEmailSignoff }) =>
        useEmailSignoff
          ? "Если у вас есть вопросы, свяжитесь с нами по адресу support@superscript.nyc.\n\nС уважением,\nSuperscript"
          : "Если у вас есть вопросы, свяжитесь с нами по адресу support@superscript.nyc.",
    },
    zh: {
      body: ({ useEmailSignoff }) =>
        useEmailSignoff
          ? "如有任何疑问，请通过 support@superscript.nyc 与我们联系。\n\n此致，\nSuperscript"
          : "如有任何疑问，请通过 support@superscript.nyc 与我们联系。",
    },
    zh_HK: {
      body: ({ useEmailSignoff }) =>
        useEmailSignoff
          ? "若有任何疑問，請聯絡我們：support@superscript.nyc。\n\n謹啟，\nSuperscript"
          : "若有任何疑問，請聯絡我們：support@superscript.nyc。",
    },
    fr: {
      body: ({ useEmailSignoff }) =>
        useEmailSignoff
          ? "Si vous avez des questions, n'hésitez pas à nous contacter à support@superscript.nyc.\n\nCordialement,\nSuperscript"
          : "Si vous avez des questions, n'hésitez pas à nous contacter à support@superscript.nyc.",
    },
    ht: {
      body: ({ useEmailSignoff }) =>
        useEmailSignoff
          ? "Si ou gen kesyon, tanpri kontakte nou nan support@superscript.nyc.\n\nAvèk Renmen,\nSuperscript"
          : "Si ou gen kesyon, tanpri kontakte nou nan support@superscript.nyc.",
    },
  },
  M_GUARANTOR_PAYMENT: {
    en: {
      body: ({ guarantorName, patientName, orgName, link }) =>
        `Hi ${guarantorName}, ${patientName} has requested you pay for their appointment at ${orgName}. Click here to pay: ${link}`,
      subject: ({ orgName, patientName }) =>
        `[Action Required] Request from ${orgName} to pay for ${patientName}'s appointment`,
    },
    fr: {
      body: ({ guarantorName, patientName, orgName, link }) =>
        `Bonjour ${guarantorName}, ${patientName} vous demande de payer pour son rendez-vous chez ${orgName}. Cliquez ici pour payer : ${link}`,
      subject: ({ orgName, patientName }) =>
        `[Action requise] Demande de ${orgName} pour payer le rendez-vous de ${patientName}`,
    },
    ht: {
      body: ({ guarantorName, patientName, orgName, link }) =>
        `Bonjou ${guarantorName}, ${patientName} mande ou peye pou randevou yo nan ${orgName}. Klike isit la pou peye: ${link}`,
      subject: ({ orgName, patientName }) =>
        `[Aksyon Obligatwa] Demann nan ${orgName} pou peye randevou ${patientName}`,
    },
  },
};

export const genericSubject: Partial<{
  [key in LanguageCodeWithFrench]: (params: MessageParams) => string;
}> = {
  en: ({ orgName }) => `Message from ${orgName}`,
  es: ({ orgName }) => `Mensaje de ${orgName}`,
  ru: ({ orgName }) => `Сообщение от ${orgName}`,
  zh: ({ orgName }) => `來自 ${orgName} 的訊息`,
  zh_HK: ({ orgName }) => `來自 ${orgName} 的訊息`,
  fr: ({ orgName }) => `Message de ${orgName}`,
  ht: ({ orgName }) => `Mesaj nan ${orgName}`,
};

export const getMessageContent = (
  key: MessageKey,
  params: MessageParams,
  language: LanguageCodeWithFrench | LanguageCode,
): { subject: string; body: string } | null => {
  const template = translatedMessageTemplates[key][language];
  if (!template) {
    datadogLogs.logger.info("Message template not found for key and language", {
      key,
      language,
    });
    return null;
  }

  const subjectFn = template.subject ?? genericSubject[language];

  const content = {
    subject: subjectFn ? subjectFn(params) : "Default Subject",
    body: template.body(params),
  };

  if (
    content.subject.includes("undefined") ||
    (content.body && (content.body as string).includes("undefined"))
  ) {
    datadogLogs.logger.error(
      "Could not send message to user, undefined found in content",
      {
        content,
        key,
        language,
        params,
      },
    );
    return null;
  }

  return content;
};
